import React, { useState } from 'react';
import { Send, Paperclip, X } from 'lucide-react';

const MessageComposer = ({
    initialData = null,
    onSend,
    onCancel
}) => {
    const [message, setMessage] = useState({
        recipient_id: initialData?.recipient_id || '',
        recipient_name: initialData?.recipient_name || '',
        subject: initialData?.subject || '',
        content: '',
        attachments: []
    });

    const [recipientSearch, setRecipientSearch] = useState('');
    const [potentialRecipients, setPotentialRecipients] = useState([]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setMessage(prev => ({ ...prev, [name]: value }));
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        setMessage(prev => ({
            ...prev,
            attachments: [...prev.attachments, ...files]
        }));
    };

    const removeAttachment = (index) => {
        setMessage(prev => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index)
        }));
    };

    const handleRecipientSearch = async (e) => {
        const searchTerm = e.target.value;
        setRecipientSearch(searchTerm);

        // TODO: 實現搜索用戶的邏輯
        // 這裡應該調用 API 搜索可能的收件人
        // 暫時模擬實現
        if (searchTerm.length > 2) {
            setPotentialRecipients([
                { id: 1, name: '張三', email: 'zhangsan@example.com' },
                { id: 2, name: '李四', email: 'lisi@example.com' }
            ]);
        } else {
            setPotentialRecipients([]);
        }
    };

    const selectRecipient = (recipient) => {
        setMessage(prev => ({
            ...prev,
            recipient_id: recipient.id,
            recipient_name: recipient.name
        }));
        setRecipientSearch('');
        setPotentialRecipients([]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // 基本驗證
        if (!message.recipient_id) {
            alert('請選擇收件人');
            return;
        }

        if (!message.subject) {
            alert('請輸入主旨');
            return;
        }

        if (!message.content) {
            alert('請輸入訊息內容');
            return;
        }

        // 創建 FormData 以支持文件上傳
        const formData = new FormData();
        formData.append('recipient_id', message.recipient_id);
        formData.append('subject', message.subject);
        formData.append('content', message.content);

        // 添加附件
        message.attachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
        });

        // 調用發送訊息的方法
        onSend(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* 標題 */}
                <div className="p-4 border-b flex justify-between items-center">
                    <h2 className="text-xl font-bold">
                        {initialData ? '回覆訊息' : '新增訊息'}
                    </h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* 表單 */}
                <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
                    <div className="p-4 space-y-4">
                        {/* 收件人選擇 */}
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                收件人
                            </label>
                            {message.recipient_name ? (
                                <div className="flex items-center bg-gray-100 p-2 rounded">
                                    <span>{message.recipient_name}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMessage(prev => ({
                                                ...prev,
                                                recipient_id: '',
                                                recipient_name: ''
                                            }));
                                        }}
                                        className="ml-auto text-red-500"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={recipientSearch}
                                        onChange={handleRecipientSearch}
                                        className="w-full border border-gray-300 rounded-md p-2"
                                        placeholder="搜尋收件人"
                                    />
                                    {potentialRecipients.length > 0 && (
                                        <div className="absolute z-10 w-full bg-white border rounded-md shadow-lg mt-1">
                                            {potentialRecipients.map(recipient => (
                                                <div
                                                    key={recipient.id}
                                                    onClick={() => selectRecipient(recipient)}
                                                    className="p-2 hover:bg-gray-100 cursor-pointer"
                                                >
                                                    {recipient.name} ({recipient.email})
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 主旨 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                主旨
                            </label>
                            <input
                                type="text"
                                name="subject"
                                value={message.subject}
                                onChange={handleInputChange}
                                className="w-full border border-gray-300 rounded-md p-2"
                                placeholder="輸入訊息主旨"
                            />
                        </div>

                        {/* 內容 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                內容
                            </label>
                            <textarea
                                name="content"
                                value={message.content}
                                onChange={handleInputChange}
                                className="w-full border border-gray-300 rounded-md p-2 h-48"
                                placeholder="輸入訊息內容"
                            />
                        </div>

                        {/* 附件 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                附件
                            </label>
                            <div className="flex items-center">
                                <input
                                    type="file"
                                    multiple
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    id="file-upload"
                                />
                                <label
                                    htmlFor="file-upload"
                                    className="flex items-center cursor-pointer bg-gray-100 hover:bg-gray-200 p-2 rounded"
                                >
                                    <Paperclip className="w-5 h-5 mr-2" />
                                    選擇檔案
                                </label>
                            </div>

                            {/* 已選擇附件列表 */}
                            {message.attachments.length > 0 && (
                                <div className="mt-2 space-y-1">
                                    {message.attachments.map((file, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center bg-gray-50 p-2 rounded"
                                        >
                                            <span className="flex-1 truncate">{file.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeAttachment(index)}
                                                className="text-red-500"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 操作按鈕 */}
                    <div className="p-4 border-t flex justify-end space-x-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            取消
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
                        >
                            <Send className="w-5 h-5 mr-2" />
                            發送
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MessageComposer;